import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLearn } from "@/contexts/LearnContext";
import { Progress } from "@/components/ui/progress";
import { ArrowUpRight, Clock, Award, BookMarked, Sparkles } from "lucide-react";

export default function HomePage() {
  const { user, content, getModuleProgress, overallProgress } = useLearn();

  const total = overallProgress();

  useEffect(() => {
    if (total >= 100) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [total]);

  if (!content) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-24 text-center text-muted-foreground">
        Loading course content…
      </div>
    );
  }

  const modules = content.modules;

  // Tetris-style span map for 10 modules
  const spanFor = (i) => {
    // alternating rhythm
    const pattern = [
      "md:col-span-7", "md:col-span-5",
      "md:col-span-4", "md:col-span-8",
      "md:col-span-6", "md:col-span-6",
      "md:col-span-5", "md:col-span-7",
      "md:col-span-8", "md:col-span-4",
    ];
    return pattern[i] || "md:col-span-6";
  };

  return (
    <div className="page-ambient">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Course Home · Welcome {user?.name?.split(" ")[0]}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              <span className="italic font-light">A guided</span> 40-hour<br />
              journey through AI &amp; ML.
            </h1>
            <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">
              Ten self-contained modules, each with sub-topics, AI-generated diagrams and two
              quizzes. Work through them in order, or jump to any topic — your progress is saved
              automatically.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="border border-border rounded p-6 bg-card" data-testid="overall-progress-card">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="uppercase tracking-[0.2em]">Your progress</span>
                <span className="font-mono text-primary text-base">{total}%</span>
              </div>
              <Progress value={total} className="mt-3 h-1.5" />
              <div className="mt-4 text-sm text-muted-foreground">
                {content.overview.total_subtopics} sub-topics · {content.overview.total_quizzes} quizzes ·{" "}
                {content.overview.total_hours} hrs
              </div>
              {total >= 100 ? (
                <div
                  className="mt-4 inline-flex items-center gap-2 text-sm text-primary"
                >
                  <Award size={14} /> Module Complete! You may now return to the main LMS dashboard to view your official certificate.
                </div>
              ) : (
                <div
                  className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground"
                  data-testid="certificate-locked-msg"
                >
                  <Award size={14} /> Certificate unlocks at 100% — {100 - total}% to go
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-2xl md:text-3xl">The 10 Modules</h2>
          <span className="text-xs text-muted-foreground">Click any card to begin</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="modules-grid">
          {modules.map((m, i) => {
            const pct = getModuleProgress(m);
            return (
              <Link
                to={`/module/${m.id}`}
                key={m.id}
                data-testid={`module-card-${m.id}`}
                className={`${spanFor(i)} group relative border border-border rounded-md p-7 bg-card hover:border-primary hover:-translate-y-0.5 transition-all flex flex-col`}
              >
                <div className="flex items-start justify-between">
                  <span className="chapter-num text-6xl md:text-7xl text-primary/80">
                    {String(m.number).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="opacity-50 group-hover:opacity-100 group-hover:text-primary transition-all" size={20} />
                </div>

                <h3 className="font-serif text-xl md:text-2xl leading-snug mt-4 mb-3">
                  {m.title}
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {m.summary}
                </p>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Clock size={12} /> {m.duration_hours} hrs
                  </span>
                  <span className="font-mono text-primary">{pct}%</span>
                </div>
                <Progress value={pct} className="h-1 mt-2" />

                <div className="mt-4 text-[11px] text-muted-foreground italic flex items-start gap-2">
                  <Sparkles size={12} className="mt-0.5 shrink-0 text-primary" />
                  <span>{m.takeaway}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="border border-border rounded p-8 md:p-12 bg-card flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <BookMarked size={28} className="text-primary shrink-0 mt-1" />
            <div>
              <h3 className="font-serif text-2xl">Tip: Bookmark anything important</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Use the bookmark icon on any sub-topic. All your bookmarks appear in the drawer on
                the top-right of every page.
              </p>
            </div>
          </div>
          <Link
            to={`/module/${modules[0].id}`}
            data-testid="start-first-module"
            className="px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded hover:brightness-110 inline-flex items-center gap-2"
          >
            Start Module 01 <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
